import { z } from 'zod';
/**
 * Context Builder — APM-H-j (`plan-harness.md` §6). Sáu lớp xếp theo độ ổn định;
 * hai điểm cắt cache (sau lớp 2, sau lớp 3). Bước `context` trong dấu vết liệt kê
 * từng khối với nguồn và số token — "prompt thiếu dữ liệu / thừa dữ liệu" đọc
 * được từ đây, không phải đoán từ kết quả.
 */
export const ContextLayer = z.number().int().min(1).max(6);
export const ContextBlock = z.object({
    layer: ContextLayer,
    /** `harness_rules`, `skill_prompt`, `org_knowledge`, `project_profile`, `ai_suggestion#…`, `ai_memory_note#…`, `clarify#…`, `user_request`. */
    source: z.string(),
    tokens: z.number().int().nonnegative(),
    /** `true` = sau khối này có `cache_control` (điểm cắt cache). */
    cacheBreakpoint: z.boolean().optional(),
});
export const ContextDropped = z.object({
    source: z.string(),
    tokens: z.number().int().nonnegative(),
    reason: z.string(),
});
/**
 * Hồ sơ dự án — lớp 3, nằm ở **system** với điểm cắt riêng. Chỉ chứa trường
 * **bất biến giữa hai run liên tiếp**: không ngày giờ hiện tại, không số task
 * đang mở, không tiến độ — chúng đổi mỗi giờ và giết cache. Test khẳng định
 * schema này không có `Date`/`count`.
 */
export const ProjectProfile = z.object({
    key: z.string(),
    name: z.string(),
    methodology: z.string(),
    /** `YYYY-MM-DD` — mốc kế hoạch của dự án, đổi vài lần một tháng. */
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    /** Trần độ dài một hạng mục tầng 1 (ngày) — quy chuẩn của dự án. */
    wbsMaxDays: z.number().int().positive(),
    taskTypes: z.array(z.object({ name: z.string(), slug: z.string() })),
    techStacks: z.array(z.object({ name: z.string(), slug: z.string() })),
    members: z.array(z.object({ name: z.string(), role: z.string() })),
    /*
      Bốn khối dưới đây thêm ở APM-H-ai. Luật chọn chỉ có một câu: **đổi chậm**.
  
      Mỗi trường ở lớp 3 nằm trong phần được cache, nên một trường đổi hàng giờ
      lọt vào đây là mất cache của **mọi** run của dự án đó — và triệu chứng là
      hoá đơn tăng, không phải một lỗi. Vì thế: `targetValue` của key result thì
      vào, `currentValue` thì không; tiêu đề tài liệu thì vào, số bản mới nhất
      thì không.
    */
    /** Mô tả dự án, cắt ~400 ký tự. Người viết một lần rồi hiếm khi sửa. */
    description: z.string().nullable().default(null),
    /** `planning | active | …` — giai đoạn hiện tại, đổi vài lần trong cả đời dự án. */
    status: z.string().default(''),
    /**
     * Objective đang mở và **đích** của từng kết quả then chốt.
     *
     * ⚠️ Không có `currentValue`: nó đổi mỗi lần ai đó báo số, và đó là thứ động
     * nhất trong cả bảng OKR. Model cần biết đội đang nhắm tới đâu, không cần
     * biết hôm nay tới đâu — muốn biết thì có tool.
     */
    objectives: z
        .array(z.object({
        title: z.string(),
        keyResults: z.array(z.object({ title: z.string(), target: z.string(), unit: z.string().nullable() })),
    }))
        .default([]),
    /**
     * **Mục lục** tài liệu: tiêu đề, id, loại, việc gắn kèm, trạng thái duyệt.
     *
     * Không có nội dung và không có số bản: nội dung đi qua `read_project_document`
     * (lớp 6), còn `latestVersionNo` đổi mỗi lần ai tải lên một bản mới.
     */
    documents: z
        .array(z.object({
        id: z.string(),
        title: z.string(),
        kind: z.string(),
        taskKey: z.string().nullable(),
        status: z.string(),
    }))
        .default([]),
});
//# sourceMappingURL=ai-context.js.map